import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { emptyFilters } from '../model/searchResults';
import type { InstrumentFilters } from '../model/searchResults';
import type { SymbolMatch } from '@biasafe.models';
import {
  instrumentExchange,
  instrumentVenue,
} from '@/features/Instruments/instrumentMetadata';
import { FilterCombobox } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/components/FilterCombobox';

/** Controlled specialist filters; the All scope never renders this toolbar. */
export function SearchInstrumentFilters({
  allInstruments,
  filters,
  count,
  onChange,
}: {
  allInstruments: Array<SymbolMatch>;
  filters: InstrumentFilters;
  count: number;
  onChange: (next: InstrumentFilters) => void;
}) {
  const { t, i18n } = useTranslation('app-shell');
  const text = (key: string, fallback: string) =>
    t(`header.${key}`, { defaultValue: fallback });
  const countryOptions = [
    ...new Set(
      allInstruments
        .map((i) => instrumentVenue(i)?.country)
        .filter((v): v is string => !!v),
    ),
  ];
  const typeOptions = [
    ...new Set(
      allInstruments.map((i) => i.securityType).filter((v): v is string => !!v),
    ),
  ];
  const exchangeOptions = [
    ...new Set(allInstruments.map(instrumentExchange).filter(Boolean)),
  ];
  const countryLabel = (code: string) =>
    new Intl.DisplayNames(
      [i18n.resolvedLanguage?.startsWith('fr') ? 'fr' : 'en'],
      { type: 'region' },
    ).of(code) ?? code;
  return (
    <div className="border-b border-border/40 bg-muted/15 px-4 py-2">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        <FilterCombobox
          allLabel={text('search_all_countries', 'All countries')}
          ariaLabel="Listing Country"
          value={filters.country}
          options={countryOptions}
          onChange={(v) => onChange({ ...filters, country: v })}
          renderOption={countryLabel}
        />
        <FilterCombobox
          allLabel={text('search_all_types', 'All types')}
          ariaLabel="Security Type"
          value={filters.type}
          options={typeOptions}
          onChange={(v) => onChange({ ...filters, type: v })}
          renderOption={(v) => (v === 'Stock' ? 'Equity' : v)}
        />
        <FilterCombobox
          allLabel={text('search_all_exchanges', 'All exchanges')}
          ariaLabel="Listing Exchange"
          value={filters.exchange}
          options={exchangeOptions}
          onChange={(v) => onChange({ ...filters, exchange: v })}
        />
        <span className="ml-auto text-xs text-muted-foreground">
          IBKR · {count} {text('search_results_count', 'instruments')}
        </span>
      </div>
      {Object.entries(filters).some(([, v]) => v !== 'All') && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {Object.entries(filters)
            .filter(([, v]) => v !== 'All')
            .map(([key, value]) => (
              <button
                key={key}
                type="button"
                className="flex items-center gap-1 rounded-md bg-primary/8 px-2 py-1 text-xs text-primary"
                aria-label={`${text('search_remove_filter', 'Remove filter')} ${value}`}
                onClick={() => onChange({ ...filters, [key]: 'All' })}
              >
                {key === 'country'
                  ? countryLabel(value)
                  : value === 'Stock'
                    ? 'Equity'
                    : value}
                <X className="size-3" />
              </button>
            ))}
          <button
            className="text-xs underline"
            onClick={() => onChange(emptyFilters)}
          >
            {text('search_clear_filters', 'Clear filters')}
          </button>
        </div>
      )}
    </div>
  );
}
